import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { FaAward, FaClipboardList, FaStickyNote, FaTrash } from 'react-icons/fa';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { PilotSurveyDialog } from './PilotSurveyDialog';

const EMPTY_NOTE = { session_label: '', session_date: '', attendees: '', notes: '' };

function NoteForm({ onSaved }) {
  const [n, setN] = useState(EMPTY_NOTE);
  const set = (k) => (e) => setN({ ...n, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/pilot/pro/notes', { ...n, attendees: n.attendees === '' ? null : Number(n.attendees), session_date: n.session_date || null });
      setN(EMPTY_NOTE);
      toast.success('Note saved — only you and the Legacy Forge admin can see it');
      onSaved();
    } catch {
      toast.error('Could not save note');
    }
  };
  const cls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm w-full';
  return (
    <form onSubmit={save} className="grid sm:grid-cols-3 gap-2 mb-3" data-testid="pilot-note-form">
      <input required placeholder="Session, e.g. Rose family intro" value={n.session_label} onChange={set('session_label')} className={cls} data-testid="pilot-note-label" />
      <input type="date" value={n.session_date} onChange={set('session_date')} className={cls} data-testid="pilot-note-date" />
      <input type="number" min="0" placeholder="People attended" value={n.attendees} onChange={set('attendees')} className={cls} data-testid="pilot-note-attendees" />
      <textarea required placeholder="What went well, questions families asked, what to change…" value={n.notes} onChange={set('notes')} rows={3} className={`${cls} sm:col-span-3`} data-testid="pilot-note-text" />
      <button type="submit" className="sm:col-span-3 bg-emerald-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-emerald-700" data-testid="pilot-note-submit">Save note</button>
    </form>
  );
}

export function PilotProCard() {
  const [data, setData] = useState(null);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const load = useCallback(() => axios.get('/api/pilot/pro/status').then(r => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  if (!data?.is_pilot) return null;
  const del = async (id) => { await axios.delete(`/api/pilot/pro/notes/${id}`); load(); };

  return (
    <div className="bg-white rounded-xl shadow-md border-l-4 border-emerald-500 p-6 mb-6" data-testid="pilot-pro-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <p className="inline-flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wide"><FaAward /> Founding Pilot Professional</p>
          <h3 className="text-xl font-bold text-gray-800 mt-1">Thank you for helping shape Legacy Forge</h3>
          <p className="text-gray-600 text-sm mt-1">Explore the platform, then tell us what you value and what's missing. Your notes are private to you and the Legacy Forge admin.</p>
        </div>
        <button onClick={() => setSurveyOpen(true)} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-700" data-testid="pilot-survey-open-btn">
          <FaClipboardList />{data.survey ? 'Update my feedback' : 'Share my feedback'}
        </button>
      </div>

      {data.presentations.length > 0 && (
        <div className="mb-4 text-sm text-gray-700" data-testid="pilot-my-presentations">
          <p className="font-semibold mb-1">Your presentations</p>
          {data.presentations.map(p => <p key={p.presentation_id}>{p.title} · {p.date} {p.time} · {p.registered} {p.registered === 1 ? 'family' : 'families'} registered</p>)}
        </div>
      )}

      <p className="font-semibold text-gray-800 flex items-center gap-2 mb-2"><FaStickyNote className="text-emerald-600" />Session notes</p>
      <NoteForm onSaved={load} />
      {data.notes.map(n => (
        <div key={n.note_id} className="bg-gray-50 rounded-lg p-3 mb-2 flex justify-between gap-3" data-testid={`pilot-note-${n.note_id}`}>
          <div>
            <p className="text-sm font-medium text-gray-800">{n.session_label}{n.session_date ? ` · ${n.session_date}` : ''}{n.attendees != null ? ` · ${n.attendees} attended` : ''}</p>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{n.notes}</p>
          </div>
          <button onClick={() => del(n.note_id)} className="text-gray-400 hover:text-red-500" aria-label="Delete note" data-testid="pilot-note-delete"><FaTrash /></button>
        </div>
      ))}
      <PilotSurveyDialog open={surveyOpen} onClose={() => setSurveyOpen(false)} initial={data.survey} onSaved={load} />
    </div>
  );
}
